import type { ReactNode } from 'react';

import { useFocusable } from '../focus';
import styles from './Chip.module.css';
import { cx } from './cx';

export interface ChipProps {
  children: ReactNode;
  /** Current value (cyan outline, Figma "Selected"). */
  selected?: boolean;
  focusKey?: string;
  disabled?: boolean;
  onSelect?: () => void;
  onFocus?: () => void;
  className?: string;
}

/** Focusable tone chip (Figma TV/Tone chip: Default / Selected / Focused). */
export function Chip({
  children,
  selected = false,
  focusKey,
  disabled = false,
  onSelect,
  onFocus,
  className,
}: ChipProps) {
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey,
    focusable: !disabled,
    onEnterPress: () => onSelect?.(),
    onFocus: () => onFocus?.(),
  });
  return (
    <div
      ref={ref}
      role="radio"
      aria-checked={selected}
      data-focus-key={key}
      data-focused={focused || undefined}
      className={cx(
        styles.chip,
        selected && styles.selected,
        focused && styles.focused,
        disabled && styles.disabled,
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface TagProps {
  children: ReactNode;
  /** `cyan` = tone label (default), `dark` = on the playing row, `muted`. */
  tone?: 'cyan' | 'dark' | 'muted';
  /** So an overlay can be `aria-describedby` its own count chip (UX-306). */
  id?: string;
  className?: string;
  'data-testid'?: string;
}

/** Static label chip ("Tono +1"), non-focusable. */
export function Tag({ children, tone = 'cyan', id, className, ...rest }: TagProps) {
  return (
    <span
      id={id}
      className={cx(styles.tag, styles[`tag-${tone}`], className)}
      data-testid={rest['data-testid']}
    >
      {children}
    </span>
  );
}
